import type { PagedModelClientChatMessage } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { NO_CACHING } from '@/configs/query';
import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import type { PagedRequest } from '@/features/api/helper/params';
import {
  getInitialPageParam,
  getNextPageParam,
  getPreviousPageParam
} from '@/features/api/helper/params';
import { useCommunicationChatApi } from '@/features/api/hooks/communication-chat/useCommunicationChatApi';
import { useAuth } from '@/features/authorization/provider/useAuth';

export const useGetChatMessages = (chatId: string) => {
  const { isAuthenticated } = useAuth();
  const api = useCommunicationChatApi();

  const select = useCallback(
    ({ pages }: InfiniteData<PagedModelClientChatMessage, PagedRequest>) =>
      pages.flatMap((page) => page.content ?? []),
    []
  );

  return useInfiniteQuery({
    queryKey: [API_CACHE_KEYS.CHAT_MESSAGES, chatId],
    queryFn: ({ pageParam }) =>
      api.getChatMessagesPaged({ chatId, ...pageParam }),
    select,
    initialPageParam: getInitialPageParam(),
    getNextPageParam,
    getPreviousPageParam,
    enabled: isAuthenticated,
    ...NO_CACHING,
    // Todo: we only get fresh data, when gcTime is set to 0
    gcTime: 0
  });
};
